// Right-hand inspector: shows the editor for whatever is selected (an element
// or a section), or a short guide when nothing is.

import React from "react";
import FeatherIcon from "feather-icons-react";
import { IconButton } from "../controls";
import { locate, findBand, ELEMENT_LABELS, ELEMENT_ICONS, elementLabel } from "../designModel";
import LayoutPanel from "./LayoutPanel";
import BandPanel from "./BandPanel";
import {
  TextPanel,
  KeyValuePanel,
  TablePanel,
  TotalsPanel,
  ImagePanel,
  ShapePanel,
  PropertiesListPanel,
  RowPanel,
  DividerPanel,
  SpacerPanel,
} from "./ContentPanels";

const PANELS = {
  text: TextPanel,
  keyValue: KeyValuePanel,
  lineTable: TablePanel,
  totals: TotalsPanel,
  image: ImagePanel,
  shape: ShapePanel,
  propertiesList: PropertiesListPanel,
  row: RowPanel,
  divider: DividerPanel,
  spacer: SpacerPanel,
};

export default function Inspector({ design, selection, ctx, actions, focusText }) {
  if (selection?.kind === "band") {
    const band = findBand(design, selection.id);
    if (band) {
      const i = design.bands.indexOf(band);
      return (
        <div className="td-inspector">
          <div className="td-inspector__title">
            <FeatherIcon icon="layout" size={15} />
            <span>{band.label}</span>
          </div>
          <BandPanel
            band={band}
            palette={design.page.palette}
            updateBand={(patch, ck) => actions.updateBand(band.id, patch, ck)}
            onConvert={(kind) => actions.convertBand(band.id, kind)}
            onDelete={() => actions.removeBand(band.id)}
            onDuplicate={() => actions.duplicateBand(band.id)}
            onMove={(dir) => actions.shiftBand(band.id, dir)}
            canMoveUp={i > 0}
            canMoveDown={i < design.bands.length - 1}
          />
        </div>
      );
    }
  }

  const loc = selection?.kind === "element" ? locate(design, selection.id) : null;
  if (!loc) {
    return (
      <div className="td-inspector td-inspector--empty">
        <FeatherIcon icon="mouse-pointer" size={22} />
        <p className="td-inspector__lead">Select anything on the page to change it.</p>
        <ul className="td-tips">
          <li>Add elements from the <strong>Elements</strong> panel — click to add, or drag onto the page.</li>
          <li>Type <strong>{"{{"}</strong> in any text to insert invoice data like the invoice number or total.</li>
          <li>Change colours for the whole template in <strong>Theme</strong>.</li>
          <li>Arrow keys nudge, Delete removes, Ctrl+D duplicates, Ctrl+Z undoes.</li>
        </ul>
      </div>
    );
  }

  const { el, band } = loc;
  const free = !!el.frame;
  const Panel = PANELS[el.type];
  const update = (patch, ck) => actions.updateElement(el.id, patch, ck);

  return (
    <div className="td-inspector">
      <div className="td-inspector__title">
        <FeatherIcon icon={ELEMENT_ICONS[el.type] || "box"} size={15} />
        <span title={ELEMENT_LABELS[el.type]}>{elementLabel(el)}</span>
        <div className="td-inspector__actions">
          {!free && (
            <>
              <IconButton icon="arrow-up" label="Move up" onClick={() => actions.shiftElement(el.id, -1)} disabled={loc.index === 0} />
              <IconButton icon="arrow-down" label="Move down" onClick={() => actions.shiftElement(el.id, 1)} disabled={loc.index === loc.list.length - 1} />
            </>
          )}
          <IconButton icon="copy" label="Duplicate (Ctrl+D)" onClick={() => actions.duplicateElement(el.id)} />
          <IconButton icon="trash-2" label="Delete (Del)" danger onClick={() => actions.removeElement(el.id)} />
        </div>
      </div>
      <div className="td-inspector__crumb">
        in <button type="button" className="td-linkbtn" onClick={() => actions.select({ kind: "band", id: band.id })}>{band.label}</button>
        {loc.row && <> › columns</>}
      </div>
      {Panel && <Panel el={el} update={update} ctx={ctx} free={free} autoFocus={focusText} />}
      <LayoutPanel
        el={el}
        band={band}
        design={design}
        update={update}
        contentWidth={ctx.contentWidth}
        onMoveToBand={(bandId) => actions.moveElement(el.id, { bandId })}
      />
    </div>
  );
}
